import { isLiabilityType, isPortfolioType } from './../shared/account-types.js';
import { formatCurrencyValue } from './../shared/format-currency.js';

export const NET_WORTH_QUERY = `query NetWorthData {
	accounts { id, name, accountType, currency, balance, principal }
}`;

/**
 * @param {Array<{ code: string, symbol: string, decimalPlaces: number, rate: number | null }>} currencies
 * @returns {Map<string, { symbol: string, decimalPlaces: number, rate: number | null }>}
 */
export function buildCurrencyMap(currencies) {
	const map = new Map();
	for (const c of currencies) {
		map.set(c.code, { symbol: c.symbol, decimalPlaces: c.decimalPlaces, rate: c.rate });
	}
	return map;
}

/**
 * @param {number} amount
 * @param {string} code
 * @param {Map<string, { symbol: string, decimalPlaces: number, rate: number | null }>} currencyMap
 * @returns {string}
 */
export function formatLocal(amount, code, currencyMap) {
	const info = currencyMap.get(code);
	if (!info) { return '\u2014'; }
	return formatCurrencyValue(amount, info.decimalPlaces, info.symbol);
}

/**
 * Convert an amount from one currency to another in terms of the user's base.
 * Returns 0 when a conversion is impossible (either currency has no known
 * rate), so unconvertible currencies contribute nothing to net-worth totals —
 * they still render natively, like points with a zero rate.
 * @param {number} amount
 * @param {string} from
 * @param {string} to
 * @param {Map<string, { symbol: string, decimalPlaces: number, rate: number | null }>} currencyMap
 * @returns {number}
 */
export function convertLocal(amount, from, to, currencyMap) {
	if (from === to) { return amount; }
	const fromEntry = currencyMap.get(from);
	const toEntry = currencyMap.get(to);
	if (!fromEntry || !toEntry || fromEntry.rate === null || toEntry.rate === null) { return 0; }
	if (fromEntry.rate === 0 || toEntry.rate === 0) { return 0; }
	return amount * fromEntry.rate / toEntry.rate;
}

/**
 * @param {Array<{ accountType: string }>} accounts
 * @returns {{ assets: any[], liabilities: any[] }}
 */
export function classifyAccounts(accounts) {
	const assets = [];
	const liabilities = [];
	for (const account of accounts) {
		if (isLiabilityType(account.accountType)) {
			liabilities.push(account);
		} else {
			assets.push(account);
		}
	}
	return { assets, liabilities };
}

/**
 * @param {Array<{ accountType: string, currency: string, balance: number }>} accounts
 * @param {(amount: number, from: string, to: string) => number} convertFn
 * @param {string} baseCurrency
 * @returns {Array<{ accountType: string, currency: string, totalBalance: number, totalConverted: number }>}
 */
export function groupByTypeAndCurrency(accounts, convertFn, baseCurrency) {
	const groups = new Map();
	for (const a of accounts) {
		const key = `${a.accountType}\0${a.currency}`;
		if (!groups.has(key)) {
			groups.set(key, { accountType: a.accountType, currency: a.currency, totalBalance: 0, totalConverted: 0 });
		}
		const g = groups.get(key);
		g.totalBalance += a.balance;
		g.totalConverted += convertFn(a.balance, a.currency, baseCurrency);
	}
	return [...groups.values()].sort((a, b) => a.accountType.localeCompare(b.accountType));
}

/**
 * @param {Array<{ accountType: string, currency: string, balance: number }>} accounts
 * @param {(amount: number, from: string, to: string) => number} convertFn
 * @param {string} baseCurrency
 * @returns {Record<string, number>}
 */
export function computeNetByType(accounts, convertFn, baseCurrency) {
	/** @type {Record<string, number>} */
	const assetTotals = {};
	/** @type {Record<string, number>} */
	const liabilityTotals = {};

	for (const a of accounts) {
		const converted = convertFn(a.balance, a.currency, baseCurrency);
		if (isLiabilityType(a.accountType)) {
			liabilityTotals[a.accountType] = (liabilityTotals[a.accountType] || 0) + converted;
		} else {
			assetTotals[a.accountType] = (assetTotals[a.accountType] || 0) + converted;
		}
	}

	/** @type {Record<string, number>} */
	const netByType = {};
	for (const [type, total] of Object.entries(assetTotals)) {
		let net = total;
		if (type === 'Bank Account') {
			net -= liabilityTotals['Credit Card'] || 0;
		} else if (type === 'Real Estate') {
			net -= liabilityTotals['Loan'] || 0;
		}
		netByType[type] = net;
	}

	return netByType;
}

/**
 * @param {Array<{ accountType: string, currency: string, balance: number }>} accounts
 * @returns {Record<string, number>}
 */
export function computeNativeNetByCurrency(accounts) {
	/** @type {Record<string, number>} */
	const totals = {};
	for (const a of accounts) {
		const sign = isLiabilityType(a.accountType) ? -1 : 1;
		totals[a.currency] = (totals[a.currency] || 0) + sign * a.balance;
	}
	return totals;
}

/**
 * @param {Array<{ accountType: string, balance: number, currency: string, principal: number | null }>} assets
 * @param {(amount: number, from: string, to: string) => number} convertFn
 * @param {string} baseCurrency
 * @returns {{ totalPrincipal: number, totalGain: number }}
 */
export function computePortfolioSummary(assets, convertFn, baseCurrency) {
	const portfolioAccounts = assets.filter((a) => isPortfolioType(a.accountType));
	const totalPrincipal = portfolioAccounts.reduce((s, a) => s + (a.principal !== null ? convertFn(a.principal, a.currency, baseCurrency) : 0), 0);
	const totalValue = portfolioAccounts.reduce((s, a) => s + convertFn(a.balance, a.currency, baseCurrency), 0);
	return {
		totalPrincipal,
		totalGain: totalValue - totalPrincipal,
	};
}
